<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {Icon} from "@iconify/vue";
import {computed, reactive, ref, watch} from "vue";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import Users from "@/components/Users.vue"
import {Select, FloatLabel, Textarea} from "primevue";
import {useConfigStore} from "@/stores/config.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {InputText} from "primevue";
import {allowDigitsDot} from '@/utils/inputValidation.js'
import {useRoute} from "vue-router";

const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const props = defineProps({
  selectedGoldStock: Object
})
const emit = defineEmits(['closeModal', 'refresh']);
const isLoading = ref(false);
const deleting = ref(false);
const saving = ref(false);
const route = useRoute();
const accountName = props.selectedGoldStock?.accountName ?? ''

function closeModal() {
  emit('closeModal');
}


const games = computed(() => configStore.getGamesByService('StocksManager') || [])
const gameObject = computed(() => games.value.find(g => g.abbreviation === route.params.game) || {})
const selectedUser = ref({})
const form = reactive({
  stock: {
    game: '',
    server: '',
    amount: '',
    notes: ''
  },
  profile: {
    id: ''
  }
})

// هر بار که selectedGoldStock تغییر کرد، فرم رو آپدیت کن
watch(
    () => props.selectedGoldStock,
    (val) => {
      if (val) {
        // حالت Edit
        form.stock.game = val.game
        form.stock.server = val.server
        form.stock.amount = val.amount
        form.stock.notes = val.notes
        form.profile.id = val.accountID
      } else {
        // حالت Add → ریست مقادیر
        form.stock.game = gameObject.value.game
        form.stock.server = ''
        form.stock.amount = ''
        form.profile.id = ''
      }
    },
    {immediate: true} // بار اول هم اجرا بشه
)
const rules = computed(() => {
  return {
    "stock": {
      "game": {required},
      "server": {required},
      "amount": {required, numeric},
    },
    "profile": {
      "id": {required}
    }
  };
});
const $vulidate = useVuelidate(rules, form);

/* ---------- Select options ---------- */


const selectedGame = computed(() =>
    games.value.find(g => g.game === form.stock.game) || null
)

const servers = computed(() =>
    selectedGame.value ? selectedGame.value.servers : []
)

async function onSave() {
  // Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  const message = props.selectedGoldStock?.accountID ? 'You are editing gold stock?' : 'You are adding new gold stock?'
  const ok = await showAlert(message, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  // API call
  try {
    saving.value = true
    const res = await ordersStore.action_addGoldStock(form)
    if (res === true) {
      showMessage('New Gold Stock added!', 'success')
      emit('refresh')
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  const ok = await showAlert('You are deleting gold stock?', 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  // API call
  try {
    deleting.value = true
    const res = await ordersStore.action_removeGoldStock({
      stock: {
        game: props.selectedGoldStock.game,
        server: props.selectedGoldStock.server
      },
      profile: {
        id: props.selectedGoldStock.accountID
      }
    })
    if (res === true) {
      showMessage('Supplier deleted!', 'success')
      emit('refresh')
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  form.stock.server = servers.value.length === 1 ? servers.value[0] : ''
}

function getSelectedUser(user) {
  selectedUser.value = user
  form.profile.id = user.id
}
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-lg h-auto text-left align-middle shadow-xl transition-all bg-white dark:bg-[#0e1726] text-black-600 dark:text-white">
              <div class="h-full flex flex-col">
                <div class="flex flex-row gap-10 justify-between px-4 border-b border-slate-700">
                  <div class="text-base py-3">
                    {{ selectedGoldStock?.accountID ? "Edit Gold Stock Amount" : "Add New Gold Stock" }}
                  </div>
                  <div class="flex flex-row items-center gap-4">
                    <div class="h-full w-[1px] bg-slate-700"></div>
                    <button
                        type="button"
                        class="text-white-dark outline-none hover:text-red-500"
                        @click="closeModal"
                    >
                      <Icon icon="pajamas:close-xs"/>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 overflow-auto h-full flex flex-col gap-6">
                  <div class="space-y-5">
                    <!-- ================== Game ================== -->
                    <template v-if="gameObject.game">
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                        <FloatLabel variant="on">
                          <InputText id="title" :value="gameObject.game" autocomplete="off" readonly disabled
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                     :class="[gameObject.game ? 'p-filled !border-slate-800' : '!border-slate-700']"/>
                          <label for="title" class="!bg-slate-900"
                                 :class="[gameObject.game ? '!text-slate-500' : '!text-slate-400']">Game</label>
                        </FloatLabel>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.stock.game.$error }"
                                    variant="on">
                          <Select v-model="form.stock.game" inputId="game" :options="games" optionLabel="game"
                                  option-value="game" @change="onGameChange"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.stock.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  filter autoFilterFocus :disabled="!!form.stock.game"/>
                          <label for="game" class="!bg-slate-900">Game</label>
                        </FloatLabel>
                        <template v-if="$vulidate.stock.game.$error">
                          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.stock.game.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                    </template>


                    <!-- ================== Server ================== -->
                    <template v-if="selectedGoldStock?.server">
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="title" :value="selectedGoldStock.server" autocomplete="off" readonly disabled
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                     :class="[selectedGoldStock.server ? 'p-filled !border-slate-800' : '!border-slate-700']"/>
                          <label for="title" class="!bg-slate-900"
                                 :class="[selectedGoldStock.server ? '!text-slate-500' : '!text-slate-400']">Server</label>
                        </FloatLabel>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.stock.server.$error }"
                                    variant="on">
                          <Select v-model="form.stock.server" inputId="server" :options="servers"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.stock.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  :virtualScrollerOptions="{ itemSize: 38 }"
                                  filter autoFilterFocus :disabled="!!form.stock.server"/>
                          <label for="server" class="!bg-slate-900">Server</label>
                        </FloatLabel>
                        <template v-if="$vulidate.stock.server.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.stock.server.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                    </template>
                    <template v-if="selectedGoldStock?.accountName">
                      <FloatLabel variant="on">
                        <InputText id="title" :value="selectedGoldStock.accountName" autocomplete="off" readonly
                                   disabled
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                   :class="[selectedGoldStock.accountName ? 'p-filled !border-slate-800' : '!border-slate-700']"/>
                        <label for="title" class="!bg-slate-900"
                               :class="[selectedGoldStock.accountName ? '!text-slate-500' : '!text-slate-400']">Supplier</label>
                      </FloatLabel>
                    </template>
                    <template v-else>
                      <Users @update:modelValue="getSelectedUser" appendTo="body"
                             label="Supplier"/>
                    </template>
                    <!-- ================== Amount ================== -->
                    <div class="col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel variant="on">
                        <InputText id="amount" v-model="form.stock.amount" autocomplete="off"
                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                   :class="{ 'has-error': $vulidate.stock.amount.$error }"
                                   @keydown="allowDigitsDot"/>
                        <label for="amount" class="!bg-slate-900"
                               :class="{ 'has-error': $vulidate.stock.amount.$error }">Amount</label>
                      </FloatLabel>
                      <template v-if="$vulidate.stock.amount.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.stock.amount.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.stock.amount.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <!-- ================== Note ================== -->
                    <div class="col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                      <FloatLabel variant="on" class="h-full">
                        <Textarea v-auto-dir id="notes" v-model="form.stock.notes" rows="5" style="resize: none"
                                  class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                        <label for="notes" class="!bg-slate-900">Notes</label>
                      </FloatLabel>
                    </div>
                  </div>
                </div>
                <div class="p-3 flex justify-end gap-3">
                  <button v-if="selectedGoldStock?.accountID" @click="onDelete" class="btn btn-outline-danger py-1">
                    <span v-if="deleting"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    delete
                  </button>
                  <button @click="onSave" class="btn btn-outline-primary py-1">
                    <span v-if="saving"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Save
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped lang="scss">

</style>